import React from 'react';
import { Dimensions, ListRenderItem, Platform, StyleSheet, View } from 'react-native';

import { useActionSheet } from '@components/ActionSheet';
import { UserAvatar } from '@components/Avatar';
import Composer from '@components/Composer';
import Explorer, { useExplorerMapDataRefreshKey } from '@components/Explorer';
import type { SearchTargets } from '@components/Explorer';
import FlatList from '@components/FlatList';
import { LargeGroupCardList } from '@components/Group/CardList';
import IconButton from '@components/IconButton';
import Image from '@components/Image';
import { isMapAvailable } from '@components/Map';
import Placeholder from '@components/Placeholders';
import { LargePlanCard } from '@components/Plan/Card';
import { PressableOpacity } from '@components/Pressable';
import ScreenContainer from '@components/ScreenContainer';
import Surface from '@components/Surface';
import { Route, TabBarVariant, TabView } from '@components/TabView';
import { Caption, Paragraph } from '@components/Typography';
import { LargeUserCardList } from '@components/User/CardList';
import { useUserListDeletion } from '@components/User/useUserListDeletion';
import { LoadingView, Row } from '@components/View';
import { useFollowing } from '@context/FollowingContext';
import { useGroups } from '@context/GroupsContext';
import { useAppTheme } from '@context/ThemeContext';
import {
  useAddPlanCommentMutation,
  usePlanCommentsQuery,
  usePlanGroupsQuery,
  usePlanLikesQuery,
  usePlanMembersQuery,
  usePlanQuery,
} from '@db/Plans/Queries';
import { RequestMutationAction, useJoinPlanRequestMutation } from '@db/Requests/Queries';
import { useUserQuery } from '@db/Users/Queries';
import { errorAlert } from '@helpers/Alert';
import { dateFormatDistanceToNowStrict } from '@helpers/Dates';
import { openReportPlan } from '@helpers/Linking';
import { getCurrentUserId } from '@helpers/firebase/Auth';
import { AppNavigation, createAppNavigation } from '@navigation/AppNavigation';
import { ViewPlanScreenToggleState as ToggleState, ViewPlanScreenProps } from '@navigation/Types';
import { Comment, DELETED_USER_INFO, Group, Plan, User } from '@types';

const PLAN_HEADER_AVATAR_SIZE = 60;
const PLAN_HEADER_DETAIL_ICON_SIZE = 18;
const EXPLORER_REGION_DELTA = 0.5;
const PLAN_SEARCH_TARGETS: SearchTargets = { users: true, groups: true, places: true };

export default function ViewPlanScreen({ route, navigation }: ViewPlanScreenProps) {
  const appNavigation = React.useMemo(() => createAppNavigation(navigation), [navigation]);
  const mapAvailable = isMapAvailable();
  const { followingUserIds } = useFollowing();
  const { userGroupIds } = useGroups();
  const planId = route.params.planId;
  const [toggleValue, setToggleValue] = React.useState<ToggleState>(
    getDefaultToggleState(mapAvailable)
  );
  const [textInput, setTextInput] = React.useState('');
  const appTheme = useAppTheme();
  const mapDataRefreshKey = useExplorerMapDataRefreshKey({ navigation, route });
  const deletedJoinedRefreshKey = String(mapDataRefreshKey ?? '');
  const planQuery = usePlanQuery({ planId, refreshKey: mapDataRefreshKey });
  const planMembersQuery = usePlanMembersQuery({ plan: planQuery.plan });
  const planGroupsQuery = usePlanGroupsQuery({ plan: planQuery.plan });
  const planCommentsQuery = usePlanCommentsQuery({ planId });
  const planLikesQuery = usePlanLikesQuery({ planId });
  const addPlanCommentMutation = useAddPlanCommentMutation();
  const joinPlanMutation = useJoinPlanRequestMutation();
  const joinedUserDeletion = useUserListDeletion<User>({
    scopeKey: `${planId}:${deletedJoinedRefreshKey}`,
    onDelete: (user, handlers) => {
      joinPlanMutation.mutate(
        { action: RequestMutationAction.DELETE, planId, userId: user.userId },
        handlers
      );
    },
  });
  const planDoc = React.useMemo(
    () => getVisiblePlan(planQuery.plan, joinedUserDeletion.deletedUserIds),
    [joinedUserDeletion.deletedUserIds, planQuery.plan]
  );
  const visiblePlanMemberUserIds = React.useMemo(
    () => new Set(planDoc ? [planDoc.userId, ...(planDoc.userIds || [])] : []),
    [planDoc]
  );
  const planUsers = React.useMemo(
    () => planMembersQuery.users.filter((user) => visiblePlanMemberUserIds.has(user.userId)),
    [planMembersQuery.users, visiblePlanMemberUserIds]
  );
  const planGroups = planGroupsQuery.groups;
  const comments = planCommentsQuery.comments;
  const likeUsers = planLikesQuery.likeUsers;
  const currentUserId = getCurrentUserId();
  const planPhotos = planDoc?.photos;
  const photos = React.useMemo(
    () => (planPhotos ? planPhotos.map((photo) => ({ uri: photo.uri })) : []),
    [planPhotos]
  );
  const ownsPlan = planDoc?.userId === currentUserId;
  const loadingUsers = planMembersQuery.loading;
  const loadingGroups = planGroupsQuery.loading;
  const loadingComments = planCommentsQuery.loading;
  const loadingLikes = planLikesQuery.loading;
  const planActionLabel = ownsPlan ? 'Edit Plan' : 'Report Plan';
  const actionSheetCallback = React.useCallback(
    (selectedIndex?: number) => {
      if (selectedIndex !== 0) return;

      if (ownsPlan) {
        appNavigation.openEditPlan(planId);
        return;
      }

      openReportPlan(planId);
    },
    [appNavigation, ownsPlan, planId]
  );
  const { showActionSheet } = useActionSheet(
    {
      options: [planActionLabel, 'Cancel'],
      cancelButtonIndex: 1,
    },
    actionSheetCallback
  );
  const explorerUserIds = React.useMemo(() => [...followingUserIds.values()], [followingUserIds]);
  const explorerGroupIds = React.useMemo(() => [...userGroupIds.values()], [userGroupIds]);

  const routeToggleState = route.params?.toggleState;

  React.useEffect(() => {
    if (routeToggleState) {
      // Route params can request a tab after this screen is already mounted.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setToggleValue(getAvailableToggleState(routeToggleState, mapAvailable));
      navigation.setParams({ toggleState: undefined });
    }
  }, [mapAvailable, navigation, routeToggleState]);

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <IconButton
          icon="dots-vertical"
          onPress={() => showActionSheet()}
          testID="navigation.Header.PlanActionsIconButton"
        />
      ),
    });
  }, [navigation, showActionSheet]);

  if (!planDoc) {
    return (
      <ScreenContainer
        loading={planQuery.loading}
        error={planQuery.error}
        errorProps={planQuery.errorProps}
      />
    );
  }

  const submitComment = (comment: string) => {
    addPlanCommentMutation.mutate(
      { comment, planId },
      {
        onSuccess: () => setTextInput(''),
        onError: (error) => errorAlert(error, { onAction: () => submitComment(comment) }),
      }
    );
  };

  const onPressComment = () => {
    if (!textInput) return;

    submitComment(textInput);
  };

  const renderMap = () => (
    <Explorer
      initialRegion={{
        ...planDoc.location,
        latitudeDelta: EXPLORER_REGION_DELTA,
        longitudeDelta: EXPLORER_REGION_DELTA,
      }}
      mapDataRefreshKey={mapDataRefreshKey}
      mapTestID="screens.ViewPlanScreen.MapView"
      onChatPress={appNavigation.openChat}
      onCreatePlanPress={appNavigation.openCreatePlan}
      onGroupPress={appNavigation.openGroup}
      onPlanNavigationPress={appNavigation.openPlan}
      onUserNavigationPress={appNavigation.openProfile}
      excludedPlanIds={[planId]}
      queryGroupIds={explorerGroupIds}
      queryUserIds={explorerUserIds}
      refreshing={loadingUsers}
      searchTargets={PLAN_SEARCH_TARGETS}
    />
  );

  const renderJoined = () => (
    <LoadingView indicatorSize="large" loading={loadingUsers} style={styles.loadingContainer}>
      <LargeUserCardList
        data={planUsers}
        deletable={(user) => ownsPlan && user.userId !== planDoc.userId}
        itemProps={{
          onPress: (user) => appNavigation.openProfile(user.userId),
          messageButton: {
            onPress: appNavigation.openChat,
          },
        }}
        onDelete={joinedUserDeletion.deleteUser}
        deleting={joinedUserDeletion.isDeleting}
        ListEmptyComponent={
          <Placeholder
            icon="account-multiple"
            title="No One Joined Yet"
            message="People who join this plan will show here."
          />
        }
        testID="screens.ViewPlanScreen.JoinedList"
      />
    </LoadingView>
  );

  const renderGroups = () => (
    <LoadingView indicatorSize="large" loading={loadingGroups} style={styles.loadingContainer}>
      <LargeGroupCardList
        data={planGroups}
        itemProps={{
          onPress: appNavigation.openGroup,
          showJoinButton: false,
        }}
        ListEmptyComponent={
          <Placeholder
            icon="account-multiple"
            title="No Groups Invited"
            message="Groups invited to this plan will show here."
          />
        }
        testID="screens.ViewPlanScreen.GroupList"
      />
    </LoadingView>
  );

  const renderComment: ListRenderItem<Comment> = ({ item }) => (
    <PlanCommentItem
      comment={item.comment}
      userId={item.userId}
      createdAt={item.createdAt}
      onUserPress={appNavigation.openProfile}
    />
  );

  const renderComments = () => (
    <LoadingView indicatorSize="large" loading={loadingComments} style={styles.loadingContainer}>
      <Composer
        value={textInput}
        onChangeText={setTextInput}
        onSend={onPressComment}
        disabled={addPlanCommentMutation.isPending || textInput.trim().length === 0}
        loading={addPlanCommentMutation.isPending}
        placeholder="Enter a comment"
        testID="screens.ViewPlanScreen.Composer.Comment"
        inputTestID="screens.ViewPlanScreen.TextInput.Comment"
        sendTestID="screens.ViewPlanScreen.IconButton.SendComment"
      />
      <FlatList
        data={comments}
        renderItem={renderComment}
        keyExtractor={(item: Comment) => item.commentId}
        style={styles.commentList}
        ListEmptyComponent={
          <Placeholder
            icon="comment"
            title="No Comments Yet"
            message="Be the first to leave a comment."
          />
        }
        testID="screens.ViewPlanScreen.CommentList"
      />
    </LoadingView>
  );

  const renderLikes = () => (
    <LoadingView indicatorSize="large" loading={loadingLikes} style={styles.loadingContainer}>
      <LargeUserCardList
        data={likeUsers}
        itemProps={{
          onPress: (user) => appNavigation.openProfile(user.userId),
          messageButton: {
            onPress: appNavigation.openChat,
          },
        }}
        ListEmptyComponent={
          <Placeholder
            icon="heart"
            title="No Likes Yet"
            message="Tap on the like icon to add a like."
          />
        }
        testID="screens.ViewPlanScreen.LikeList"
      />
    </LoadingView>
  );

  const renderPhoto: ListRenderItem<{ uri: string }> = ({ item }) => (
    <View
      style={[
        styles.imageContainer,
        { width: Math.min(Dimensions.get('window').width, appTheme.maxWidth) },
      ]}>
      <Image
        source={item}
        style={[styles.image, { borderRadius: appTheme.roundness }]}
        contentFit="cover"
      />
    </View>
  );

  const renderPhotos = () => (
    <FlatList
      data={photos}
      renderItem={renderPhoto}
      horizontal
      keyExtractor={(item, index) => index.toString()}
      // paging not working on web
      pagingEnabled={Platform.select({ default: true, web: false })}
      ListEmptyComponent={
        <Placeholder
          title="No Photos Yet"
          icon="image"
          message="The plan owner has not added any photos."
        />
      }
      style={[
        styles.photosList,
        { maxWidth: Platform.select({ default: undefined, web: appTheme.maxWidth }) },
      ]}
      testID="screens.ViewPlanScreen.FlatList.Photos"
    />
  );

  const renderNotes = () => (
    <View style={styles.notesContainer} testID="screens.ViewPlanScreen.NotesContainer">
      {planDoc.notes ? (
        <Surface style={styles.notesSurface}>
          <Paragraph style={styles.notesHeaderText}>Notes</Paragraph>
          <Paragraph>{planDoc.notes}</Paragraph>
        </Surface>
      ) : (
        <Placeholder icon="text" title="No Notes" message="Ask a question in the comments." />
      )}
    </View>
  );

  const routes = getRoutes(mapAvailable);
  const trackedToggleValue = getAvailableToggleState(toggleValue, mapAvailable);
  const headerLoading = loadingUsers || loadingGroups;

  const renderScene = ({ route }: { route: Route }) => {
    switch (route.key) {
      case ToggleState.JOINED:
        return renderJoined();
      case ToggleState.GROUPS:
        return renderGroups();
      case ToggleState.LIKES:
        return renderLikes();
      case ToggleState.COMMENTS:
        return renderComments();
      case ToggleState.NOTES:
        return renderNotes();
      case ToggleState.MAP:
        return renderMap();
      case ToggleState.PHOTOS:
        return renderPhotos();
      default:
        return null;
    }
  };

  return (
    <ScreenContainer
      testID="screens.ViewPlanScreen.Background"
      style={styles.background}
      error={planQuery.error}
      errorProps={planQuery.errorProps}>
      <LoadingView
        indicatorSize="small"
        loading={headerLoading}
        style={styles.planHeaderLoading}
        testID="screens.ViewPlanScreen.Header.Loading">
        <PlanHeader
          plan={planDoc}
          planGroups={planGroups}
          planUsers={planUsers}
          appNavigation={appNavigation}
        />
      </LoadingView>
      <TabView
        routes={routes}
        renderScene={renderScene}
        tabBarVariant={TabBarVariant.SMALL}
        tabBarScrollEnabled
        initiallyTrackRouteKey={trackedToggleValue}
        onIndexChange={(index) => setToggleValue(routes[index].key as ToggleState)}
      />
    </ScreenContainer>
  );
}

const AVATAR_SIZE = 30;

type PlanCommentItemProps = {
  comment: string;
  userId: string;
  createdAt: Date;
  onUserPress: (userId: string) => void;
};

function PlanCommentItem({ comment, userId, createdAt, onUserPress }: PlanCommentItemProps) {
  const isDeletedUserComment = userId === DELETED_USER_INFO.userId;
  const { loading, user: fetchedUserDoc } = useUserQuery({
    enabled: !isDeletedUserComment,
    userId,
  });
  const userDoc = isDeletedUserComment ? DELETED_USER_INFO : fetchedUserDoc;

  if (!userDoc) {
    return loading ? (
      <LoadingView loading={loading} indicatorSize="small" style={styles.commentContainer} />
    ) : null;
  }

  return (
    <LoadingView loading={loading} indicatorSize="small" style={styles.commentContainer}>
      <Row>
        <PressableOpacity
          disabled={isDeletedUserComment}
          onPress={isDeletedUserComment ? undefined : () => onUserPress(userId)}
          style={styles.commentAvatarContainer}>
          <UserAvatar
            displayName={userDoc?.displayName}
            photoUrl={userDoc?.photoUrl}
            size={AVATAR_SIZE}
          />
        </PressableOpacity>
        <View style={styles.commentTextContainer}>
          <Caption style={styles.commentText}>
            {userDoc?.displayName} · {dateFormatDistanceToNowStrict(createdAt) + ' ago'}
          </Caption>
          <Paragraph style={styles.commentText}>{comment}</Paragraph>
        </View>
      </Row>
    </LoadingView>
  );
}

type PlanHeaderProps = {
  appNavigation: Pick<AppNavigation, 'openChat' | 'openPlan' | 'openProfile'>;
  plan: Plan;
  planGroups: Group[];
  planUsers: User[];
};

function PlanHeader({ appNavigation, plan, planGroups, planUsers }: PlanHeaderProps) {
  return (
    <LargePlanCard
      actionButtonStyle={styles.planHeaderActionButton}
      actionContentContainerStyle={styles.planHeaderActions}
      avatarLimit={4}
      avatarSize={PLAN_HEADER_AVATAR_SIZE}
      data={plan}
      detailIconSize={PLAN_HEADER_DETAIL_ICON_SIZE}
      detailTextStyle={styles.planHeaderDetailText}
      detailsStyle={styles.planHeaderDetails}
      groupProfiles={planGroups}
      onChatPress={appNavigation.openChat}
      onPlanPress={appNavigation.openPlan}
      onUserPress={appNavigation.openProfile}
      participantUsers={planUsers}
      rowStyle={styles.planHeaderRow}
      showCommentAction={false}
      onPress={null}
      style={styles.planHeader}
      testID="screens.ViewPlanScreen.Header.PlanCard"
    />
  );
}

function getDefaultToggleState(mapAvailable: boolean) {
  return mapAvailable ? ToggleState.MAP : ToggleState.JOINED;
}

function getAvailableToggleState(toggleState: ToggleState, mapAvailable: boolean) {
  if (toggleState === ToggleState.MAP && !mapAvailable) return getDefaultToggleState(mapAvailable);

  return toggleState;
}

function getVisiblePlan(plan: Plan | null, deletedJoinedUserIds: ReadonlySet<string>) {
  if (!plan || deletedJoinedUserIds.size === 0) return plan;

  return {
    ...plan,
    userIds: (plan.userIds || []).filter((userId) => !deletedJoinedUserIds.has(userId)),
  };
}

function getRoutes(mapAvailable: boolean): Route[] {
  const routes: Route[] = [];

  if (mapAvailable) {
    routes.push({ title: 'Map', key: ToggleState.MAP });
  }

  routes.push(
    { title: 'Joined', key: ToggleState.JOINED },
    { title: 'Groups', key: ToggleState.GROUPS },
    { title: 'Comments', key: ToggleState.COMMENTS },
    { title: 'Likes', key: ToggleState.LIKES },
    { title: 'Photos', key: ToggleState.PHOTOS },
    { title: 'Notes', key: ToggleState.NOTES }
  );

  return routes;
}

const styles = StyleSheet.create({
  background: {
    alignItems: 'stretch',
  },
  commentAvatarContainer: {
    alignSelf: 'flex-start',
    marginTop: 5,
  },
  commentContainer: {
    padding: 5,
  },
  commentList: {
    flex: 1,
  },
  commentText: {
    flex: 1,
  },
  commentTextContainer: {
    flex: 1,
    marginLeft: 5,
  },
  image: {
    height: '100%',
    width: '100%',
  },
  imageContainer: {
    height: '100%',
    padding: 5,
  },
  loadingContainer: {
    flex: 1,
  },
  notesContainer: {
    flex: 1,
    width: '100%',
  },
  notesHeaderText: {
    fontWeight: 'bold',
  },
  notesSurface: {
    margin: 5,
    padding: 10,
    paddingHorizontal: 20,
  },
  photosList: {
    alignSelf: 'center',
  },
  planHeader: {
    flexShrink: 0,
    paddingTop: 10,
    width: '100%',
  },
  planHeaderActionButton: {
    marginHorizontal: 2,
  },
  planHeaderActions: {
    paddingHorizontal: 5,
  },
  planHeaderDetailText: {
    fontSize: 16,
    letterSpacing: 0,
    lineHeight: 20,
  },
  planHeaderDetails: {
    gap: 3,
    marginHorizontal: 6,
  },
  planHeaderLoading: {
    flexShrink: 0,
    minHeight: 126,
    width: '100%',
  },
  planHeaderRow: {
    paddingHorizontal: 4,
  },
});
